import { useState } from 'react';
import toast from 'react-hot-toast';
import { z } from 'zod';
import { getErrorFromCatch, serializeFieldErrors } from '../api/api';
import { IPatchOrderInfo, orderApi } from '../api/order.api';
import { IOrder, IOrderItem } from '../model/IOrder';
import { msg } from '../msg';
import checkFormStateByZod from '../utils/checkFormStateByZod';

export type TTrackingState = {
  carrier: string;
  trackingNum: string;
};

export type TTrackingErrors = Partial<Record<keyof TTrackingState, string>>;

// --
// -- Form schemas
// --
// -- The same two rules the CS portal applies. A one-character carrier or
// -- tracking number is a slip of the keyboard, and the server would accept
// -- it, mark the item Shipped and email the customer about it.
// --
const trackingInfoScheme = z.object({
  carrier: z
    .string({ required_error: msg('trackinginfo-no-carrier') })
    .min(2, msg('trackinginfo-short-carrier')),
  trackingNum: z
    .string({ required_error: msg('trackinginfo-no-tracking') })
    .min(2, msg('trackinginfo-short-tracking')),
} as Record<keyof TTrackingState, any>);

export default function useOrder() {
  const [loading, setLoading] = useState<boolean>(false);
  const [tryToPatchOrder] = orderApi.usePatchOrderMutation();

  // -- null is success; anything else is the field errors to show.
  const patchOrder = async (order: IPatchOrderInfo): Promise<Record<string, string> | null> => {
    try {
      setLoading(() => true);
      await tryToPatchOrder(order).unwrap();
      return null;
    } catch (err) {
      toast.error(`${msg('failed-to-add-tracking-info')} ${getErrorFromCatch(err)}`);
      return serializeFieldErrors(err) ?? { message: getErrorFromCatch(err) };
    } finally {
      setLoading(() => false);
    }
  };

  // --
  // -- One carrier and tracking number applied to every item on the order.
  // --
  // -- Sent as a single PATCH rather than one per item: the server walks the
  // -- list, collects whatever it actually changed and sends the customer one
  // -- shipping confirmation covering all of it. Item-by-item requests would
  // -- send one email per item.
  // --
  const patchAllProductsTrackings = async (
    data: TTrackingState,
    order: IOrder,
  ): Promise<TTrackingErrors | null> => {
    const errors = checkFormStateByZod(trackingInfoScheme, data);
    if (errors) return errors;

    const orderItems = (order?.orderItems || []).map(({ id }) => ({
      id,
      carrier: data?.carrier,
      trackingNum: data?.trackingNum,
    }));

    const errs = await patchOrder({ orderId: order.orderId, orderItems });
    return errs ? { carrier: errs.message || msg('unknown-error') } : null;
  };

  // -- The same, for one item.
  const patchProductTracking = async (
    data: TTrackingState,
    orderItem: IOrderItem,
  ): Promise<TTrackingErrors | null> => {
    const errors = checkFormStateByZod(trackingInfoScheme, data);
    if (errors) return errors;

    const errs = await patchOrder({
      orderId: orderItem.orderId,
      orderItems: [{ id: orderItem.id, carrier: data?.carrier, trackingNum: data?.trackingNum }],
    });
    return errs ? { carrier: errs.message || msg('unknown-error') } : null;
  };

  return { patchOrder, patchAllProductsTrackings, patchProductTracking, loading };
}
